import { useEffect, useState } from "react";
import PageHero from "@/components/PageHero";
import { FadeUp } from "@/components/Motion";
import SeoHead from "@/components/SeoHead";

const chapters = [
  {
    num: "01",
    letter: "P",
    title: "Profile Your Objectives",
    body: "The strategy is set by the client, not by the market. Every engagement begins with a structured intake of what the client is trying to achieve — financially and personally.",
    items: ["Financial goals", "Existing properties", "Lifestyle needs", "Investment horizon", "Risk appetite", "Long-term objectives"],
  },
  {
    num: "02",
    letter: "I",
    title: "Investigate the Market",
    body: "Every recommendation is contextualised against the current cycle. We consult primary sources — URA, HDB, MAS, IRAS, CPF Board — and layer market intelligence over the client's specific position.",
    items: ["Market cycles", "Pricing trends", "Policies", "Supply and demand", "Financing environment", "Economic indicators"],
  },
  {
    num: "03",
    letter: "R",
    title: "Review Opportunities and Risks",
    body: "Alternatives are modelled — including doing nothing. Each option is measured for affordability, upside and downside, with clear articulation of the trade-offs.",
    items: ["Affordability", "Returns", "Rental demand", "Capital growth potential", "Downside risks", "Alternative scenarios"],
  },
  {
    num: "04",
    letter: "A",
    title: "Advise the Best Strategy",
    body: "The team converges on a single recommendation. Sequencing, financing structure and timing are all specified — with dependencies and decision points made explicit.",
    items: ["Purchase", "Sale", "Upgrade", "Restructuring", "Investment", "Financing", "Exit planning"],
  },
  {
    num: "05",
    letter: "C",
    title: "Create Long-Term Wealth",
    body: "The engagement continues after the transaction. Portfolios are reviewed against policy changes and life events, and rebalanced when the strategy calls for it.",
    items: ["Portfolio monitoring", "Asset optimisation", "Risk management", "Equity planning", "Future opportunities", "Legacy considerations"],
  },
];

export default function Framework() {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const sections = chapters.map((_, i) => document.getElementById(`fw-${i}`));
      const y = window.scrollY + window.innerHeight * 0.35;
      let idx = 0;
      sections.forEach((s, i) => {
        if (s && s.offsetTop <= y) idx = i;
      });
      setActive(idx);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <SeoHead pageKey="framework" />
      <PageHero
        eyebrow="The Framework™"
        chapter="Chapter — Methodology"
        lines={["The P.I.R.A.C.", "Property Advisory Framework."]}
        blurb="From Research to Results. Every property decision, backed by evidence. Five chapters, one methodology — applied uniformly to every engagement."
      />

      <section className="bg-[#060f1e] pb-32 relative">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid grid-cols-12 gap-8">
            {/* Sticky index */}
            <aside className="hidden lg:block col-span-3">
              <div className="sticky top-32 space-y-3">
                <div className="overline mb-6">Index</div>
                {chapters.map((c, i) => (
                  <a
                    key={c.num}
                    href={`#fw-${i}`}
                    className={`flex items-center gap-4 py-2 border-l transition-all duration-500 pl-4 ${
                      active === i ? "border-[#cca24d] text-[#f7f5f0]" : "border-[#1a2940] text-[#9ca3af]/60"
                    }`}
                    data-testid={`framework-nav-${i}`}
                  >
                    <span className="font-serif text-lg tnum">{c.num}</span>
                    <span className="text-sm">{c.letter} — {c.title}</span>
                  </a>
                ))}
              </div>
            </aside>

            {/* Chapters */}
            <div className="col-span-12 lg:col-span-9 space-y-32">
              {chapters.map((c, i) => (
                <div key={c.num} id={`fw-${i}`} className="relative pt-16">
                  <div className="absolute -top-8 right-0 lg:right-8 watermark-num select-none pointer-events-none">
                    {c.letter}
                  </div>
                  <FadeUp>
                    <div className="flex items-center gap-6 mb-8">
                      <span className="font-serif text-4xl text-[#cca24d] tnum">{c.num}</span>
                      <span className="h-px flex-1 max-w-[120px] bg-[#cca24d]/40" />
                      <span className="overline">Chapter {c.num}</span>
                    </div>
                  </FadeUp>

                  <FadeUp delay={0.1}>
                    <div className="flex items-baseline gap-6 mb-8">
                      <div className="w-20 h-20 lg:w-28 lg:h-28 flex items-center justify-center border border-[#cca24d]/60 flex-shrink-0">
                        <span className="font-serif text-5xl lg:text-6xl text-[#cca24d]">{c.letter}</span>
                      </div>
                      <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#f7f5f0] font-light tracking-tight leading-[0.95]">{c.title}</h2>
                    </div>
                  </FadeUp>

                  <FadeUp delay={0.15}>
                    <p className="max-w-3xl text-[#d8d8d8] text-base md:text-lg leading-relaxed mb-10">{c.body}</p>
                  </FadeUp>

                  <FadeUp delay={0.2}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#1a2940] border border-[#1a2940]">
                      {c.items.map((it, ix) => (
                        <div
                          key={it}
                          className={`bg-[#0c1728] p-6 flex items-baseline gap-5 ${
                            // An odd number of items would otherwise leave the
                            // final cell empty, and the grid's gap colour shows
                            // through it as a solid tile that reads as a fault.
                            ix === c.items.length - 1 && c.items.length % 2 === 1
                              ? "md:col-span-2"
                              : ""
                          }`}
                        >
                          <span className="text-xs text-[#cca24d]/60 tnum">{String(ix + 1).padStart(2, "0")}</span>
                          <span className="font-serif text-lg text-[#f7f5f0]">{it}</span>
                        </div>
                      ))}
                    </div>
                  </FadeUp>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ----------------------------------------------- The four frameworks ---
        Four separate frameworks, not four sheets of one — the artwork was
        previously all filed under P.R.U.D.E.N.C.E., which was wrong. Titles and
        descriptions come from the client's Framework.docx and sit above each
        sheet, so the section reads without the reader having to decode dense
        artwork first.

        The sheets are wide and detailed, so each scrolls horizontally on small
        screens rather than shrinking to the point of illegibility. Opening one
        in a new tab gives the full-resolution version.
      */}
      <section className="bg-[#0c1728] border-t border-[#1a2940] py-24 lg:py-32" data-testid="framework-frameworks">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <FadeUp><span className="overline">The PIRAC Frameworks</span></FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl lg:text-6xl text-[#f7f5f0] font-light tracking-tight max-w-3xl leading-[1.05]">
              Four frameworks, one <em className="text-[#cca24d] not-italic font-normal italic">discipline.</em>
            </h2>
          </FadeUp>
          <FadeUp delay={0.15}>
            <p className="mt-8 max-w-3xl text-lg text-[#d8d8d8] font-light leading-relaxed">
              Each framework addresses a different part of the same question — how to plan, research,
              structure and grow property wealth with discipline rather than guesswork.
            </p>
          </FadeUp>

          <div className="mt-20 space-y-24">
            {[
              {
                src: "/assets/framework-prudence.jpeg",
                overline: "Property Wealth Planning",
                title: "P.R.U.D.E.N.C.E. — A Disciplined Strategy for Lasting Wealth",
                description:
                  "A structured property wealth planning framework designed to guide every stage of your journey. " +
                  "From planning and research to diversification, asset enhancement and exit planning, P.R.U.D.E.N.C.E. " +
                  "helps you make informed decisions while building sustainable wealth for yourself and future generations.",
                alt: "The P.R.U.D.E.N.C.E. property wealth planning framework — plan wisely, research thoroughly, understand your position, diversify strategically, enhance your assets, navigate opportunities, compound wealth, exit with purpose",
              },
              {
                src: "/assets/framework-research.jpeg",
                overline: "The Research Framework",
                title: "R.E.S.E.A.R.C.H. — Turning Market Insights into Smarter Decisions",
                description:
                  "A research-driven framework that transforms market intelligence into clear property strategies. " +
                  "By reviewing your position, evaluating opportunities, executing strategically and continuously refining " +
                  "your approach, R.E.S.E.A.R.C.H. helps reduce uncertainty, manage risk and uncover opportunities for long-term growth.",
                alt: "The R.E.S.E.A.R.C.H. framework — review, evaluate, strategise, execute, assess, refine, compound, harvest",
              },
              {
                src: "/assets/framework-strategy.jpeg",
                overline: "The Strategy System",
                title: "S.T.R.A.T.E.G.Y. — Your Roadmap to Wealth Freedom",
                description:
                  "An 8-step strategic advisory system that brings structure and confidence to your property decisions. " +
                  "From studying the market and clarifying your goals to assessing risk, structuring assets and timing your moves, " +
                  "S.T.R.A.T.E.G.Y. provides a personalised roadmap towards stronger assets, sustainable income and long-term financial freedom.",
                alt: "The S.T.R.A.T.E.G.Y. system — study and analyse, target and clarify, risk assessment, asset strategy, time your move, execute and structure, grow and optimise, yield and legacy",
              },
              {
                src: "/assets/framework-plan-and-grow.jpeg",
                overline: "The Wealth Framework",
                title: "PLAN & GROW — From Property Planning to Financial Freedom",
                description:
                  "A holistic wealth framework that connects where you are today with where you want to be tomorrow. " +
                  "Through clear goals, careful analysis, strategic leverage, disciplined action and long-term wealth building, " +
                  "PLAN & GROW helps turn property decisions into a purposeful journey towards greater choice, security and financial freedom.",
                alt: "The PLAN & GROW wealth framework — wealth vision, explore options, assess and analyse, leverage strategically, take action, harvest wealth",
              },
            ].map((f, i) => (
              <FadeUp key={f.src} delay={0.05 * i}>
                <article>
                  <span className="overline">{f.overline}</span>
                  <h3 className="mt-5 font-serif text-2xl md:text-3xl text-[#f7f5f0] font-light tracking-tight leading-snug max-w-4xl">
                    {f.title}
                  </h3>
                  <p className="mt-5 mb-10 max-w-4xl text-[#b9c2d0] font-light leading-relaxed">
                    {f.description}
                  </p>
                  <a
                    href={f.src}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block border border-[#1a2940] hover:border-[#cca24d]/50 transition-colors"
                    aria-label={`${f.title} — open full size`}
                  >
                    <img
                      src={f.src.replace(/\.jpeg$/, "-1536.webp")}
                      srcSet={
                        f.src.replace(/\.jpeg$/, "-800.webp") + " 800w, " +
                        f.src.replace(/\.jpeg$/, "-1536.webp") + " 1536w"
                      }
                      sizes="(min-width: 1024px) 94vw, 92vw"
                      alt={f.alt}
                      className="w-full h-auto block"
                      loading="lazy"
                      width="1535"
                      height="1024"
                    />
                  </a>
                  <a
                    href={f.src}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 min-h-11 py-2 inline-flex items-center gap-2 text-sm text-[#9ca3af] hover:text-[#cca24d] transition-colors"
                  >
                    <span className="h-px w-6 bg-[#cca24d]" />
                    Open the full-size diagram
                  </a>
                </article>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
